import * as Linking from 'expo-linking';
import { Platform } from 'react-native';

type LocationParams = {
  latitude?: number | null;
  longitude?: number | null;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
};

/**
 * Builds a full address string from location parts
 */
const buildAddressString = (location: LocationParams): string => {
  const parts = [
    location.name,
    location.address,
    location.city,
    location.state,
    location.postalCode,
  ].filter(Boolean);
  return parts.join(', ');
};

/**
 * Opens the device's maps application with the given location.
 *
 * Priority:
 * 1. Uses coordinates (latitude/longitude) if available - most accurate
 * 2. Falls back to address search if no coordinates
 *
 * Platform behavior:
 * - iOS: Opens Apple Maps (with fallback to Google Maps web)
 * - Android: Opens default maps app via geo: URI
 * - Web: Opens Google Maps in browser
 *
 * @param location - Location object with optional coordinates and address parts
 * @returns Promise that resolves when URL is opened, or rejects on failure
 */
export const openMapsLocation = async (
  location: LocationParams,
): Promise<void> => {
  const hasCoordinates =
    location.latitude != null && location.longitude != null;
  const addressString = buildAddressString(location);

  if (!hasCoordinates && !addressString) {
    console.warn('openMapsLocation: No coordinates or address provided');
    return;
  }

  const encodedAddress = encodeURIComponent(addressString);
  let url: string;

  if (hasCoordinates) {
    const { latitude, longitude } = location;
    const label = location.name ? encodeURIComponent(location.name) : '';

    if (Platform.OS === 'ios') {
      // Apple Maps with coordinates and optional label
      url = label
        ? `maps:?q=${label}&ll=${latitude},${longitude}`
        : `maps:?ll=${latitude},${longitude}`;
    } else if (Platform.OS === 'android') {
      // Android geo URI - opens default maps app
      url = label
        ? `geo:${latitude},${longitude}?q=${latitude},${longitude}(${label})`
        : `geo:${latitude},${longitude}`;
    } else {
      // Web - Google Maps
      url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    }
  } else {
    // Address-based search
    if (Platform.OS === 'ios') {
      url = `maps:?q=${encodedAddress}`;
    } else if (Platform.OS === 'android') {
      url = `geo:0,0?q=${encodedAddress}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }
  }

  try {
    // On web, open in new tab
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const canOpen = await Linking.canOpenURL(url);

    if (!canOpen) {
      // Fallback to Google Maps web URL
      const fallbackUrl = hasCoordinates
        ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}`
        : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

      await Linking.openURL(fallbackUrl);
      return;
    }

    await Linking.openURL(url);
  } catch (error) {
    console.error('openMapsLocation: Failed to open maps URL', error);
  }
};
